/* ============================================================================
   FORZAR LA ACTUALIZACIÓN

   La aplicación se instala como PWA y guarda una copia de sí misma para poder
   abrir sin señal. Eso es lo que hace falta en vuelo, pero tiene un costo: si
   esa copia queda atascada, el dispositivo sigue mostrando una versión vieja
   por más que el servidor tenga otra.

   Pasó, y caro: durante ocho rondas de correcciones el iPad siguió sirviendo
   una compilación de días atrás desde su propia caché. Cada prueba medía otra
   cosa, y desde afuera parecía que las correcciones no funcionaban.

   Con `autoUpdate` no debería volver a ocurrir. Esto es la salida de
   emergencia para cuando ocurra igual: borra la copia guardada, da de baja el
   servicio de trabajo y recarga.

   Es puro y recibe el entorno por parámetro, así que se puede probar sin
   navegador — que es donde estas cosas se rompen sin avisar.
   ========================================================================== */

/**
 * ¿Se puede forzar la actualización en este navegador?
 *
 * Sin servicio de trabajo no hay copia guardada que borrar, y el botón sería
 * una promesa vacía.
 */
export function sePuedeForzar(entorno = globalThis) {
  return Boolean(entorno?.navigator?.serviceWorker) || Boolean(entorno?.caches);
}

/**
 * Borra la copia guardada y recarga.
 *
 * Devuelve qué se hizo, para poder decírselo al piloto y para poder probarlo.
 * Nunca lanza: si una parte falla, sigue con las otras — la recarga es lo que
 * más importa y va al final.
 *
 * @param {object} [entorno] window, o un doble en las pruebas
 * @returns {Promise<{registrosDeBaja:number, cachesBorradas:number, recargo:boolean, errores:string[]}>}
 */
export async function forzarActualizacion(entorno = globalThis) {
  const resultado = { registrosDeBaja: 0, cachesBorradas: 0, recargo: false, errores: [] };

  /* Primero el servicio de trabajo: mientras siga registrado, puede volver a
     llenar la caché con lo viejo apenas se borre. */
  try {
    const sw = entorno?.navigator?.serviceWorker;
    if (sw?.getRegistrations) {
      const registros = await sw.getRegistrations();
      for (const registro of registros) {
        try {
          if (await registro.unregister()) resultado.registrosDeBaja += 1;
        } catch (e) {
          resultado.errores.push(`no se pudo dar de baja el servicio: ${e.message || e}`);
        }
      }
    }
  } catch (e) {
    resultado.errores.push(`no se pudo consultar el servicio de trabajo: ${e.message || e}`);
  }

  try {
    if (entorno?.caches?.keys) {
      const claves = await entorno.caches.keys();
      for (const clave of claves) {
        try {
          if (await entorno.caches.delete(clave)) resultado.cachesBorradas += 1;
        } catch (e) {
          resultado.errores.push(`no se pudo borrar «${clave}»: ${e.message || e}`);
        }
      }
    }
  } catch (e) {
    resultado.errores.push(`no se pudieron listar las cachés: ${e.message || e}`);
  }

  /* La recarga va al final y siempre, aunque lo anterior haya fallado: sin
     ella el piloto se queda mirando la misma pantalla vieja sin entender qué
     pasó. */
  try {
    if (typeof entorno?.location?.reload === "function") {
      entorno.location.reload();
      resultado.recargo = true;
    }
  } catch (e) {
    resultado.errores.push(`no se pudo recargar: ${e.message || e}`);
  }

  return resultado;
}
